/**
 * Typed error surface for the CWOS API client (doc 10.3). Every non-OK response
 * throws a typed error so screens can branch on `status` / `kind` rather than
 * parsing message strings. A 401 always surfaces as {@link UnauthorizedError}
 * so the app can redirect to sign-in (doc 01 A0.3).
 *
 * Every error also carries the `requestId` the transport sent as `x-request-id`
 * (see transport.ts). That is the SAME value the API used as its Fastify request
 * id, echoed back on the response and threaded into every server log line for
 * the request — so a failure a person is looking at on screen can be joined to
 * the server's own record of it. Before this existed a user-reported bug had no
 * route to its logs at all: the client generated nothing and sent nothing.
 */
export class ApiError extends Error {
    status;
    body;
    /**
     * The `x-request-id` for the call that failed, or null when the error was
     * constructed outside the transport. Safe to show a user verbatim: it is a
     * random identifier and carries no data of its own.
     */
    requestId;
    constructor(status, message, body, requestId = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
        this.requestId = requestId;
    }
}
/** Thrown on HTTP 401 — the session has ended; the app redirects to `/signin`. */
export class UnauthorizedError extends ApiError {
    constructor(message, body, requestId = null) {
        super(401, message, body, requestId);
        this.name = 'UnauthorizedError';
    }
}
/** Thrown on HTTP 403 — authenticated but missing the reading permission. */
export class ForbiddenError extends ApiError {
    /**
     * The permission the API named at the TOP LEVEL of the body, when it named
     * one. Deliberately not filled in from {@link refusals}: a ceiling refusal
     * can name several keys for several different reasons, and folding them into
     * one string is the collapse this class exists to avoid.
     */
    permission;
    /**
     * THE STRUCTURED REFUSALS, when the body carried them.
     *
     * Every authority-granting door in the product — role grants, permission
     * grants, supervisor reassignment, delegation, coverage, role composition —
     * refuses through `PrivilegeCeilingError`, whose 403 body has NO top-level
     * `permission`: the keys live in `refusals[].permissionKey` alongside the
     * closed `code` and the `scope` at fault. The transport read only
     * `body.permission`, so `permission` was null for that entire family and the
     * only thing that survived was `message` — a run-on join of every refusal's
     * sentence, from which no screen can recover which key failed for which
     * reason.
     *
     * `null` means THE BODY DID NOT CARRY THE STRUCTURE (an ordinary 403), which
     * is not the same as a refusal with no reasons — the server cannot produce
     * one, since a ceiling error is constructed from a non-empty list. An empty
     * array would say something the wire never says.
     */
    refusals;
    /**
     * THE 403 IS ABOUT THE PERSON, NOT ABOUT THE KEY — `body.restricted === true`.
     *
     * `apps/api/src/domains/eligibility/guard.ts` refuses eight route families for
     * a worker under an eligibility hold. That refusal is NOT a missing
     * permission: the account holds every key it always held, nobody can grant
     * anything to fix it, and the remedy is a supervisor lifting a hold. It is the
     * one 403 in the product where "ask your administrator for the permission" is
     * actively wrong advice.
     *
     * Without this flag the two are indistinguishable to a client — both are 403
     * with a `message` — so every screen said the permission sentence, and a held
     * worker met a refusal with nothing anywhere explaining it. The server has
     * been sending `restricted: true` all along precisely so a client would not
     * have to string-match {@link ApiError.message}; nothing read it.
     *
     * `false` means the body did not say so, which is every other 403. It says a
     * restriction APPLIES and nothing about what it is — no category, no scope, no
     * hold id, no date. Those are deliberately not on the wire (see the guard's
     * own comment), and a client must not go looking for them: the worker's whole
     * entitlement is {@link ApiError.message}, which is a server-side constant.
     */
    restricted;
    constructor(message, body, permission, requestId = null, refusals = null, restricted = false) {
        super(403, message, body, requestId);
        this.name = 'ForbiddenError';
        this.permission = permission;
        this.refusals = refusals;
        this.restricted = restricted;
    }
}
/**
 * Is this failure an ELIGIBILITY RESTRICTION rather than a missing permission?
 *
 * STRUCTURAL, NOT `instanceof`, for the reason `LoadFailure.httpStatusOf` and
 * the worker app's `failureKind` are: three unrelated classes named `ApiError`
 * reach these screens and none is `instanceof` either of the others. What they
 * share is the parsed body, so the flag is read from there — which also makes
 * this correct for a transport this module has never heard of.
 *
 * Guarded on the status as well as the flag: a body that happens to carry
 * `restricted: true` on a 200 or a 500 is not a refusal, and a predicate that
 * answered yes for one would put the worker's restricted screen in front of
 * somebody whose request actually succeeded.
 */
export function isRestrictedByEligibility(err) {
    if (typeof err !== 'object' || err === null)
        return false;
    const status = err.status;
    if (status !== 403)
        return false;
    const flagged = err.restricted;
    if (flagged === true)
        return true;
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return false;
    return body.restricted === true;
}
/**
 * Every permission key a 403 named, from wherever it named them, WITHOUT
 * flattening the reasons.
 *
 * For a plain 403 this is the single top-level `permission`; for a ceiling
 * refusal it is every `permissionKey` the refusals carry, deduplicated and in
 * the order the server listed them. A refusal that is about no key at all
 * (`self_grant`, `superuser_tier_not_administrable`) contributes nothing, and
 * an EMPTY result on a ceiling refusal is therefore meaningful: the request was
 * refused for a reason that is not about a permission, so offering to go and
 * ask for one is the wrong sentence.
 */
export function refusedPermissionKeys(err) {
    const keys = [];
    const push = (key) => {
        if (key !== null && key.length > 0 && !keys.includes(key))
            keys.push(key);
    };
    push(err.permission);
    for (const refusal of err.refusals ?? [])
        push(refusal.permissionKey);
    return keys;
}
/**
 * Thrown on HTTP 404 — the API will not say that this thing exists.
 *
 * NOT ALWAYS "YOU TYPED THE WRONG URL". Several domains answer 404
 * DELIBERATELY, as concealment, where a 403 would itself be the disclosure: a
 * knowledge article outside the reader's scope, an attachment addressed in a
 * namespace that is not the caller's, a signup route that is switched off and
 * 404s precisely so that probing it cannot confirm the endpoint exists. A
 * screen that renders "not found" as a bug report is wrong about all three.
 *
 * It carries no extra field. {@link ForbiddenError} has `permission` because a
 * 403 body names the permission the caller lacked; a 404 body names nothing by
 * construction — that is the point of it — so there is nothing to lift.
 */
export class NotFoundError extends ApiError {
    constructor(message, body, requestId = null) {
        super(404, message, body, requestId);
        this.name = 'NotFoundError';
    }
}
export function isUnauthorized(err) {
    return err instanceof UnauthorizedError;
}
export function isForbidden(err) {
    return err instanceof ForbiddenError;
}
export function isNotFound(err) {
    return err instanceof NotFoundError;
}
//# sourceMappingURL=errors.js.map